import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { twMerge } from 'tailwind-merge'

import { useAuthInfo } from '@sweetauth/core'

import { DocxViewer } from '@/components/editor-v3/DocxViewer'
import type {
  DocumentNavigationHandle,
  DocxSelectionReveal,
  DocxViewerHandle,
  DocxViewerPageState,
} from '@/components/editor-v3/DocxViewer'
import DocumentLoadProgress, {
  LOAD_LABELS,
} from '@/components/editor-v3/ui/DocumentLoadProgress'
import { LatestVersionNotice } from '@/components/files/DocxFilePreview/LatestVersionNotice'
import { usePreviewFreshness } from '@/components/files/DocxFilePreview/usePreviewFreshness'
import { useEditorsInRoom } from '@/components/files/FileEditors/useEditorsInRoom'

import type { DocumentParts } from '@/helpers/docx/parts'
import { describeDownload, downloadFraction } from '@/helpers/downloadProgress'
import { cleanFileName } from '@/helpers/file'

import type { DocxVersionSource } from '@/hooks/files/types'
import {
  useDownloadingDocxRecord,
  useFileDocxBlob,
} from '@/hooks/files/useFileDocxBlob'
import {
  type PackageSlimmer,
  usePartialPackage,
} from '@/hooks/files/usePartialFile'
import { useReplaceFileContent } from '@/hooks/files/useReplaceFileContent'
import { usePermissionCheck } from '@/hooks/permissions/usePermissionCheck'

import { slimDocx } from '@/lib/ooxml/slim/docx'

import { useFileDownloadProgress } from '@/stores/FileDownloadStore'

import type { File as SweetFile } from '@/types/File'
import type { UUID } from '@/types/Id'
import type { Permission } from '@/types/Permission'

import {
  useReportUnsavedChanges,
  useUnsavedChangesGuard,
} from '@/services/unsaved-changes/context'

export interface DocxFilePreviewProps {
  file: SweetFile
  /** One version instead of the file as it stands. Read-only: a comment would land on the current document. */
  version?: DocxVersionSource | null
  className?: string
  /** Handle for scrolling the document from outside it; narrow, so one ref can point at a file or a PDF. */
  viewerRef?: React.Ref<DocumentNavigationHandle>
  /** On screen, with as many pages laid out as the count says; the rest arrive as the reader goes. */
  onReady?: (pageCount: number) => void
  /** The document could not be opened; the host is expected to fall back to what it showed before. */
  onLoadError?: (error: Error) => void
  /** The laid-out page total as it grows. `onReady` carries the first count; this carries the rest. */
  onPageCountChange?: (pageCount: number) => void
  /** The 1-based page under the reader — see DocxViewerProps. */
  onCurrentPageChange?: (page: number) => void
  /** What the reader has highlighted, and the paraId it starts in. Passing it starts selection tracking. */
  onSelectionChange?: (text: string | null, paragraphId: string | null) => void
  /**
   * Whether this surface offers commenting. A host whose selection means
   * something else — chat context — turns it off, skipping the writability check.
   */
  allowComments?: boolean
  /** Host-driven deselect: a bump collapses the viewer's own selection, which outlives the DOM one. */
  selectionResetNonce?: number
  /** A captured chat selection to reveal once the document is open — see DocxViewerProps. */
  revealSelection?: DocxSelectionReveal | null
  /** The revealed selection's highlight went away — see DocxViewerProps. */
  onRevealDismissed?: (key: number) => void
}

const WRITE_PERMISSIONS: Permission[] = ['write']

/**
 * Past this size the download is a wait worth filling: the document's opening
 * is read by range and shown, read-only, until the whole file has arrived and
 * laid out behind it.
 */
export const OPENING_FIRST_MIN_BYTES = 2 * 1024 * 1024
/** About a page and a half of an ordinary document; the reader scrolls into the rest once it is there. */
const OPENING_BLOCKS = 60
export const slimOpening: PackageSlimmer = (zip, name) =>
  slimDocx(zip, name, { blocks: OPENING_BLOCKS })

const selectCanWrite = (permissions: Map<Permission, boolean>) =>
  permissions.get('write') === true

interface OpenedDocument {
  /** The file, or the version of it, whose bytes these are. */
  key: string
  blob: Blob
}

/**
 * A stored Word file, shown as itself: the reader sees the tracked changes and
 * comments their colleagues left, and can answer them. Everyone with write
 * access to the file may comment; everyone else reads it.
 */
export const DocxFilePreview: React.FC<DocxFilePreviewProps> = ({
  file,
  version = null,
  className,
  viewerRef,
  onReady,
  onLoadError,
  onPageCountChange,
  onCurrentPageChange,
  onSelectionChange,
  selectionResetNonce,
  allowComments = true,
  revealSelection,
  onRevealDismissed,
}) => {
  const authInfo = useAuthInfo()
  // The document on screen: the file, or the one version of it being read.
  const openKey = version?.id ?? file.id
  const { data: stored, error } = useFileDocxBlob({ fileId: file.id, version })
  const download = useFileDownloadProgress(openKey)

  // The opening stays over the document until the document has pages to show,
  // or has said it will not open, when the document's own failure shows instead.
  const [laidOutKey, setLaidOutKey] = useState<string | null>(null)
  const [failedKey, setFailedKey] = useState<string | null>(null)
  const settled = laidOutKey === openKey || failedKey === openKey
  const openingWanted =
    version === null &&
    !settled &&
    !error &&
    (file.size ?? 0) > OPENING_FIRST_MIN_BYTES
  // Read from the record the download is reading, and kept once the download
  // is done with it: the one handed in may sign for a revision somebody has
  // since replaced.
  const { data: downloading } = useDownloadingDocxRecord({ fileId: file.id })
  const [openingSource, setOpeningSource] = useState<{
    key: string
    record: SweetFile
  } | null>(null)
  if (openingWanted && downloading && openingSource?.key !== openKey) {
    setOpeningSource({ key: openKey, record: downloading })
  }
  const openingRecord =
    openingSource?.key === openKey ? openingSource.record : null
  const { slimFile: opening } = usePartialPackage({
    file: openingRecord ?? file,
    slim: slimOpening,
    enabled: openingWanted && openingRecord !== null,
    // The whole file is on its way already; a server that ignores the range
    // would send a second copy.
    rangesOnly: true,
  })
  const showOpening = opening !== null && !settled && !error

  const commentsOffered = allowComments && version === null
  const { data: canWrite, isError: permissionFailed } = usePermissionCheck({
    resourceType: 'file',
    resourceId: file.id as UUID,
    permissions: WRITE_PERMISSIONS,
    select: selectCanWrite,
    enabled: commentsOffered,
  })
  const canComment = commentsOffered && canWrite === true

  const { mutateAsync: replaceContent } = useReplaceFileContent({
    fileId: file.id,
    pursuitId: file.pursuit_file?.pursuit_id ?? '',
  })

  const { outdated, showLatest } = usePreviewFreshness({
    fileId: file.id,
    pursuitId: file.pursuit_file?.pursuit_id ?? '',
    heldVersionId: stored?.versionId ?? null,
    enabled: version === null,
  })

  // A save here sends the whole document, so whichever of two lands second takes
  // the other's comments back out. Kept apart from `allowComments`, which is
  // settled as the document is built and would re-open it under a reader partway
  // through a review.
  const editors = useEditorsInRoom({ fileId: file.id, enabled: canComment })
  const whoIsEditing = editors?.description ?? null
  // Somebody being in the room is what holds comments back; where to go instead
  // rides along. Waiting on the destination would leave the button live.
  const editorHref = editors?.href
  const commentsPaused = useMemo(
    () =>
      canComment && whoIsEditing
        ? {
            reason: `${whoIsEditing} Their next save will overwrite any comment you leave here.`,
            href: editorHref,
          }
        : null,
    [canComment, whoIsEditing, editorHref],
  )

  // Catching up drops the copy on screen and every unsaved comment in it. Pausing
  // covers the version this preview can see coming; one landing from an upload,
  // a restore or another tab is the one it cannot.
  const guardUnsaved = useUnsavedChangesGuard()
  const handleShowLatest = useCallback(
    () => guardUnsaved(showLatest),
    [guardUnsaved, showLatest],
  )

  // The bytes the viewer opened, pinned: a save writes its document back into
  // the same cache entry, and handing those bytes to the viewer that wrote them
  // would tear the live document down under the reader. Taken during render so
  // the first frame with bytes in hand is the document, not more spinner.
  // Dropped when the cache entry is: a new version is the file, and this is not.
  const [opened, setOpened] = useState<OpenedDocument | null>(null)
  if (stored) {
    if (opened?.key !== openKey) {
      setOpened({ key: openKey, blob: stored.blob })
    }
  } else if (opened) {
    setOpened(null)
  }

  useEffect(() => {
    if (error) {
      onLoadError?.(error)
    }
  }, [error, onLoadError])

  // Which file it was said of, rather than a flag and an effect to clear the flag.
  const readyReportedRef = useRef<string | null>(null)
  const handlePageCountChange = useCallback(
    (state: DocxViewerPageState) => {
      if (state.pageCount < 1) {
        return
      }
      setLaidOutKey(openKey)
      onPageCountChange?.(state.pageCount)
      if (readyReportedRef.current === openKey) {
        return
      }
      readyReportedRef.current = openKey
      onReady?.(state.pageCount)
    },
    [openKey, onReady, onPageCountChange],
  )

  const commentAuthor = useMemo(() => {
    const name = `${authInfo?.user?.firstName ?? ''} ${
      authInfo?.user?.lastName ?? ''
    }`.trim()
    const email = authInfo?.user?.email
    return { name: name || email || 'Me', email }
  }, [
    authInfo?.user?.firstName,
    authInfo?.user?.lastName,
    authInfo?.user?.email,
  ])

  const handleLoadError = useCallback(
    (loadError: Error) => {
      setFailedKey(openKey)
      onLoadError?.(loadError)
    },
    [openKey, onLoadError],
  )

  const handleSave = useCallback(
    async (parts: DocumentParts) => {
      await replaceContent(parts)
    },
    [replaceContent],
  )

  // The viewer's handle answers more than a preview's hosts ask for.
  const viewer = useRef<DocxViewerHandle>(null)
  useImperativeHandle(
    viewerRef,
    () => ({
      scrollToPage: (pageNumber: number) =>
        viewer.current?.scrollToPage(pageNumber),
      getBoundingClientRect: () =>
        viewer.current?.getBoundingClientRect() ?? null,
    }),
    [],
  )

  // A review lives in this tab until it is saved, and every way out of a file is one click.
  const [dirty, setDirty] = useState(false)
  useReportUnsavedChanges(`file:${file.id}`, dirty)

  const openedBlob = opened?.key === openKey ? opened.blob : null

  const openingViewer = showOpening && (
    <DocxViewer
      className="h-full w-full"
      file={opening}
      fileName={cleanFileName(file.name)}
      fileKey={`opening:${openKey}`}
      allowComments={false}
    />
  )

  // Both answers are waited for: whether comments are offered is settled as the
  // document is built, so arriving late would re-open it. Only the first answer,
  // though — the check re-resolves after every write the app makes, and one that
  // failed has said all it is going to say. A file nobody may comment on is a
  // better answer than a file nobody may read.
  const waiting =
    !openedBlob ||
    (commentsOffered && canWrite === undefined && !permissionFailed)
  if (waiting && !openingViewer) {
    return (
      // Painted on the canvas the document lands on, so an open reads as one surface settling.
      <div
        className={twMerge(
          'editor-v3-canvas editor-v3-viewer h-full w-full',
          className,
        )}
      >
        {openedBlob || !download ? (
          <DocumentLoadProgress label={LOAD_LABELS.opening} />
        ) : (
          <DocumentLoadProgress
            label={LOAD_LABELS.downloading}
            progress={downloadFraction(download)}
            detail={describeDownload(download)}
          />
        )}
      </div>
    )
  }

  return (
    // Above the document rather than over its first lines. A page is fitted
    // across the pane and not down it, so losing height costs the reader nothing.
    <div className={twMerge('relative flex h-full w-full flex-col', className)}>
      {!waiting && (
        <>
          {version === null && (
            <LatestVersionNotice
              fileId={file.id}
              outdated={outdated}
              onShowLatest={handleShowLatest}
            />
          )}
          <DocxViewer
            className="h-auto min-h-0 flex-1"
            file={openedBlob}
            fileName={file.name}
            fileKey={openKey}
            viewerRef={viewer}
            onPageCountChange={handlePageCountChange}
            allowComments={canComment}
            commentsPaused={commentsPaused}
            commentAuthor={commentAuthor}
            onSave={canComment ? handleSave : undefined}
            onDirtyChange={setDirty}
            onCurrentPageChange={onCurrentPageChange}
            onSelectionChange={onSelectionChange}
            selectionResetNonce={selectionResetNonce}
            revealSelection={revealSelection}
            onRevealDismissed={onRevealDismissed}
            onLoadError={handleLoadError}
          />
        </>
      )}
      {openingViewer && (
        // Over the document while it downloads and lays out beneath, in one
        // place throughout so it is never built twice.
        <div className="absolute inset-0 z-10 bg-white dark:bg-primary-900">
          {openingViewer}
        </div>
      )}
    </div>
  )
}
